import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Layers, AlertTriangle, Sparkles, Trophy, TrendingDown, Minus, Check, BadgeCheck, Loader2, Crown, Calendar, ArrowRight, X, Clock, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatOdds, PARLAY_DISCLAIMER } from "@/lib/parlay";
import { formatPT } from "@/lib/timezone";
import UpgradeModal from "@/components/UpgradeModal";
import { startParlayCheckout } from "@/lib/billing";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerClose } from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Leg {
  id: string;
  leg_order: number;
  game_description: string;
  pick_description: string;
  line: number | null;
  odds: number | null;
  reasoning: string | null;
  commence_time?: string | null;
  sports?: { code: string } | null;
}

function LegStartTime({ commence_time }: { commence_time?: string | null }) {
  if (!commence_time) return null;
  const t = new Date(commence_time);
  if (isNaN(t.getTime())) return null;
  const started = t.getTime() <= Date.now();
  if (started) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-success/40 bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success">
        <PlayCircle className="h-2.5 w-2.5" /> Started
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
      <Clock className="h-2.5 w-2.5" /> {formatPT(t)}
    </span>
  );
}

interface Props {
  parlay: {
    id: string;
    title: string;
    total_odds: number | null;
    suggested_units: number;
    risk: string;
    ai_summary: string | null;
    correlation_notes: string | null;
    leg_count: number;
    betting_date?: string | null;
    result?: string | null;
    units_delta?: number | null;
  };
  legs: Leg[] | null;
  locked: boolean;
  canBuyParlay: boolean;
  /** Free user already purchased today's parlay */
  parlayPurchased?: boolean;
  /** Pro or admin */
  isPremium?: boolean;
}

function ResultChip({ result, units }: { result?: string | null; units?: number | null }) {
  if (!result || result === "pending" || result === "void") return null;
  const u = Number(units ?? 0);
  if (result === "won") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-success">
        <Trophy className="w-3 h-3" /> Won{u ? ` +${u.toFixed(2)}u` : ""}
      </span>
    );
  }
  if (result === "lost") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-danger/40 bg-danger/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-danger">
        <TrendingDown className="w-3 h-3" /> Lost {u.toFixed(2)}u
      </span>
    );
  }
  if (result === "pushed") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-muted-foreground/30 bg-muted/30 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <Minus className="w-3 h-3" /> Push
      </span>
    );
  }
  return null;
}

export default function ParlayOfDayCard({ parlay, legs, locked, canBuyParlay, parlayPurchased, isPremium }: Props) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const sortedLegs = useMemo(() => [...(legs ?? [])].sort((a, b) => a.leg_order - b.leg_order), [legs]);

  const sportCodes = useMemo(() => {
    const codes = (legs ?? []).map((l) => l.sports?.code).filter(Boolean) as string[];
    return Array.from(new Set(codes));
  }, [legs]);
  const isMultiSport = sportCodes.length > 1;

  // Fallback fetch: when legs are hidden by RLS (locked users), pull just commence times.
  const [fallbackTimes, setFallbackTimes] = useState<Date[] | null>(null);
  const hasLegTimes = (legs ?? []).some((l) => !!l.commence_time);
  useEffect(() => {
    if (hasLegTimes || !parlay.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("parlay_leg_start_times", { _parlay_id: parlay.id });
      if (cancelled || !data) return;
      const ts = (data as Array<{ commence_time: string | null }>)
        .map((r) => (r.commence_time ? new Date(r.commence_time) : null))
        .filter((d): d is Date => !!d && !isNaN(d.getTime()));
      setFallbackTimes(ts);
    })();
    return () => { cancelled = true; };
  }, [parlay.id, hasLegTimes]);

  const startSummary = useMemo(() => {
    const fromLegs = (legs ?? [])
      .map((l) => (l.commence_time ? new Date(l.commence_time) : null))
      .filter((d): d is Date => !!d && !isNaN(d.getTime()));
    const times = fromLegs.length > 0 ? fromLegs : (fallbackTimes ?? []);
    if (times.length === 0) return null;
    const earliest = new Date(Math.min(...times.map((d) => d.getTime())));
    const now = Date.now();
    const startedCount = times.filter((d) => d.getTime() <= now).length;
    return { earliest, startedCount, total: times.length, allStarted: startedCount === times.length, anyStarted: startedCount > 0 };
  }, [legs, fallbackTimes]);

  const handleBuyParlay = async () => {
    if (buying) return;
    setBuying(true);
    const tid = toast.loading("Opening secure checkout…");
    try {
      await startParlayCheckout(parlay.id);
      toast.success("Redirecting to checkout…", { id: tid });
    } catch (e: any) {
      toast.error(e?.message ?? "Unable to start parlay checkout", { id: tid });
    } finally {
      setBuying(false);
    }
  };

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/[0.08] via-card to-card shadow-[0_10px_40px_-12px_hsl(35_95%_58%/0.25)]">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-accent via-primary to-accent" />
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />

        {/* Header */}
        <div className="relative flex flex-wrap items-center justify-between gap-2 border-b border-accent/20 bg-accent/[0.04] px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-accent">
            <Layers className="h-4 w-4" />
            Parlay of the Day
            {parlay.betting_date && (
              <span className="ml-1 text-muted-foreground/80 font-semibold tracking-wider">
                · {new Date(parlay.betting_date + "T12:00:00Z").toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric" })} PT
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <ResultChip result={parlay.result} units={parlay.units_delta} />
            {!locked && parlayPurchased && !isPremium && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-success">
                <BadgeCheck className="w-3 h-3" /> Parlay Access Purchased
              </span>
            )}
            {isPremium && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <Crown className="w-3 h-3" /> Pro
              </span>
            )}
            {startSummary && (
              startSummary.allStarted ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-success">
                  <PlayCircle className="w-3 h-3" /> In Play
                </span>
              ) : startSummary.anyStarted ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-success">
                  <PlayCircle className="w-3 h-3" /> {startSummary.startedCount}/{startSummary.total} Started
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-[11px] font-semibold tracking-wider text-muted-foreground">
                  <Clock className="w-3 h-3" /> First leg {formatPT(startSummary.earliest)}
                </span>
              )
            )}
            <span className="te-chip border border-accent/30 bg-accent/10 text-accent capitalize text-[11px]">
              {parlay.risk} variance
            </span>
          </div>
        </div>

        {/* Status banner — visible whether locked or unlocked */}
        {startSummary && (!parlay.result || parlay.result === "pending") && (
          startSummary.allStarted ? (
            <div className="relative flex items-start gap-3 border-b border-success/30 bg-success/10 px-4 py-2.5 sm:px-6">
              <PlayCircle className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <div className="min-w-0 flex-1 text-xs sm:text-sm">
                <div className="font-bold uppercase tracking-wider text-success">All legs in play</div>
                <div className="text-muted-foreground">Every leg has started. This parlay can no longer be purchased.</div>
              </div>
            </div>
          ) : startSummary.anyStarted ? (
            <div className="relative flex items-start gap-3 border-b border-warning/30 bg-warning/10 px-4 py-2.5 sm:px-6" style={{ backgroundColor: "hsl(var(--accent) / 0.10)", borderColor: "hsl(var(--accent) / 0.35)" }}>
              <PlayCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <div className="min-w-0 flex-1 text-xs sm:text-sm">
                <div className="font-bold uppercase tracking-wider text-accent">{startSummary.startedCount} of {startSummary.total} legs started</div>
                <div className="text-muted-foreground">Parlay is partially in play — purchases are closed once any leg starts.</div>
              </div>
            </div>
          ) : (
            <div className="relative flex items-start gap-3 border-b border-border/60 bg-secondary/30 px-4 py-2.5 sm:px-6">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1 text-xs sm:text-sm">
                <div className="font-bold uppercase tracking-wider text-foreground">Not started</div>
                <div className="text-muted-foreground">First leg starts {formatPT(startSummary.earliest)} · {startSummary.total} legs pending</div>
              </div>
            </div>
          )
        )}

        {/* Body */}
        <div className="relative p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight break-words">
                {locked ? `${parlay.leg_count}-Leg Parlay` : parlay.title}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {isMultiSport ? (
                  <span className="te-chip border border-primary/30 bg-primary/10 text-primary text-[10px] uppercase tracking-wider">
                    Multi-sport
                  </span>
                ) : null}
                {sportCodes.map((c) => (
                  <span
                    key={c}
                    className="te-chip border border-border bg-secondary/40 text-[10px] uppercase tracking-wider text-muted-foreground"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 sm:flex sm:items-center sm:gap-4 sm:text-right shrink-0">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Odds</div>
                <div className="font-display text-lg sm:text-xl font-bold te-grad-text">
                  {formatOdds(parlay.total_odds)}
                </div>
              </div>
              <div className="hidden sm:block h-8 w-px bg-border" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Units</div>
                <div className="font-display text-lg sm:text-xl font-bold">
                  {locked ? "—" : `${parlay.suggested_units}u`}
                </div>
              </div>
              <div className="hidden sm:block h-8 w-px bg-border" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Legs</div>
                <div className="font-display text-lg sm:text-xl font-bold">{parlay.leg_count}</div>
              </div>
            </div>
          </div>

          {!locked && parlay.ai_summary && (
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{parlay.ai_summary}</p>
          )}

          {locked ? (
            <div className="mt-5">
              <div className="mb-4 text-center">
                <div className="font-display text-lg font-semibold">Two ways to access today's parlay</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Get the full {parlay.leg_count}-leg breakdown, total odds, and analysis.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {/* One-time: Parlay only */}
                {canBuyParlay && (
                  <div className="relative flex flex-col rounded-xl border border-accent/40 bg-gradient-to-b from-accent/[0.08] to-card p-4 sm:p-5">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="font-display text-sm font-bold uppercase tracking-wider text-accent">
                        Today's Parlay
                      </div>
                      <span className="rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                        One-time
                      </span>
                    </div>
                    <div className="mb-3 flex items-baseline gap-1">
                      <span className="font-display text-3xl font-bold">$2.99</span>
                      <span className="text-xs text-muted-foreground">/ today</span>
                    </div>
                    <ul className="mb-4 space-y-1.5 text-xs text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                        <span>Today's parlay only</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                        <span>All {parlay.leg_count} legs with reasoning</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Calendar className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                        <span>Expires at end of day</span>
                      </li>
                    </ul>
                    <Button
                      onClick={handleBuyParlay}
                      disabled={buying || Boolean(startSummary?.anyStarted)}
                      className="mt-auto w-full bg-gradient-accent text-accent-foreground hover:opacity-90"
                      size="sm"
                    >
                      {startSummary?.anyStarted ? (
                        <><PlayCircle className="mr-2 h-4 w-4" /> Parlay already started</>
                      ) : buying ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…</>
                      ) : (
                        <><Sparkles className="mr-2 h-4 w-4" /> Unlock for $2.99</>
                      )}
                    </Button>
                    {startSummary?.anyStarted && (
                      <p className="mt-2 text-center text-[11px] text-muted-foreground">
                        One-time purchase closes once any leg goes live.
                      </p>
                    )}
                  </div>
                )}

                {/* Pro */}
                <div className="relative flex flex-col rounded-xl border border-primary/40 bg-gradient-to-b from-primary/[0.08] to-card p-4 sm:p-5">
                  <div className="absolute -top-2 right-3 rounded-full border border-primary/40 bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-sm">
                    Best value
                  </div>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="font-display text-sm font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                      <Crown className="h-3.5 w-3.5" /> Go Pro
                    </div>
                    <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                      Monthly
                    </span>
                  </div>
                  <div className="mb-3 flex items-baseline gap-1">
                    <span className="font-display text-3xl font-bold">$19.99</span>
                    <span className="text-xs text-muted-foreground">/ month</span>
                  </div>
                  <ul className="mb-4 space-y-1.5 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>All Top 5 picks, every day</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>Parlay of the Day included</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>Full analysis & reasoning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>Premium history & insights</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => setUpgradeOpen(true)}
                    variant="outline"
                    size="sm"
                    className="mt-auto w-full border-primary/40 hover:bg-primary/10"
                  >
                    <Crown className="mr-2 h-4 w-4" /> Go Pro — $19.99/mo
                  </Button>
                </div>
              </div>

              <p className="mt-3 text-center text-[11px] text-muted-foreground">
                Cancel anytime · Secure checkout via Stripe
              </p>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              {/* Compact leg previews */}
              <ul className="divide-y divide-border/50 overflow-hidden rounded-xl border border-border/60 bg-secondary/20">
                {sortedLegs.map((leg, i) => (
                  <li key={leg.id} className="flex items-start gap-3 p-3 sm:p-3.5">
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[11px] font-bold text-accent">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {leg.sports?.code && (
                          <span className="te-chip border border-border/60 bg-background/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                            {leg.sports.code}
                          </span>
                        )}
                        <span className="font-semibold text-sm leading-tight break-words">{leg.pick_description}</span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground break-words">
                        <span>{leg.game_description}</span>
                        <LegStartTime commence_time={leg.commence_time} />
                      </div>
                      {leg.reasoning && (
                        <p className="mt-1 text-[11px] leading-snug text-muted-foreground/85 line-clamp-2">
                          {leg.reasoning}
                        </p>
                      )}
                    </div>
                    <span className="te-chip shrink-0 border border-border bg-background font-mono text-[11px]">
                      {formatOdds(leg.odds)}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => setDrawerOpen(true)}
                className="w-full bg-gradient-accent text-accent-foreground hover:opacity-95"
                size="sm"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                View Full Parlay Analysis
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              {parlayPurchased && !isPremium && (
                <div className="flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/[0.06] p-3 text-xs">
                  <Crown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">Want every Top 5 pick too?</div>
                    <div className="text-muted-foreground">Pro includes the parlay daily plus full Top 5 access for $19.99/mo.</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setUpgradeOpen(true)} className="border-primary/40 hover:bg-primary/10">
                    Go Pro
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex items-start gap-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>High variance. Entertainment-first. {PARLAY_DISCLAIMER}</span>
          </div>
        </div>
      </div>
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="max-h-[92vh] flex flex-col">
          <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col px-4 pb-2 pt-2">
            <div className="flex items-start justify-between gap-3 border-b border-border/60 pb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-accent">
                  <Layers className="h-3.5 w-3.5" /> Full Parlay Analysis
                </div>
                <h3 className="mt-1 font-display text-lg sm:text-xl font-bold leading-tight break-words">
                  {parlay.title}
                </h3>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className="te-chip border border-accent/30 bg-accent/10 text-accent capitalize text-[10px]">
                    {parlay.risk} variance
                  </span>
                  <span>·</span>
                  <span>{parlay.leg_count} legs</span>
                  <span>·</span>
                  <span className="font-mono text-foreground/80">{formatOdds(parlay.total_odds)}</span>
                  <span>·</span>
                  <span>{parlay.suggested_units}u suggested</span>
                </div>
              </div>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </DrawerClose>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1" data-vaul-no-drag>
              <div className="space-y-3 py-4">
                {parlay.ai_summary && (
                  <p className="text-sm leading-relaxed text-muted-foreground">{parlay.ai_summary}</p>
                )}

                {sortedLegs.map((leg, i) => (
                  <div key={leg.id} className="rounded-xl border border-border/60 bg-secondary/20 p-3 sm:p-4">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/20 text-[10px] font-bold text-accent">
                          {i + 1}
                        </span>
                        {leg.sports?.code && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {leg.sports.code}
                          </span>
                        )}
                      </div>
                      <span className="te-chip shrink-0 border border-border bg-background font-mono text-xs">
                        {formatOdds(leg.odds)}
                      </span>
                    </div>
                    <div className="font-semibold text-sm break-words">{leg.pick_description}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground break-words">
                      <span>{leg.game_description}</span>
                      <LegStartTime commence_time={leg.commence_time} />
                    </div>
                    {leg.reasoning && (
                      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{leg.reasoning}</p>
                    )}
                  </div>
                ))}

                {parlay.correlation_notes && (
                  <div className="rounded-xl border border-border/60 bg-background p-3 text-sm">
                    <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Correlation notes</div>
                    {parlay.correlation_notes}
                  </div>
                )}

                <div className="rounded-xl border border-border/60 bg-secondary/30 p-3 text-xs">
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Unit guidance</div>
                  <div className="text-sm font-semibold">{parlay.suggested_units}u suggested · {parlay.risk} variance</div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Sizing reflects model confidence on the parlay as a whole. Adjust to your own bankroll rules.
                  </p>
                </div>

                <div className="flex items-start gap-2 rounded-xl border border-border/60 bg-background/40 p-3 text-[11px] text-muted-foreground">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>High variance. Entertainment-first. Parlays are high-variance and should be treated as entertainment-first. {PARLAY_DISCLAIMER}</span>
                </div>
              </div>
            </div>

            <div className="border-t border-border/60 pt-3 pb-1">
              <DrawerClose asChild>
                <Button variant="outline" size="sm" className="w-full">Close</Button>
              </DrawerClose>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
